import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import { Box, Typography, Chip, Link, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import apiService from '../../services/apiService';
import {formatPlayerName, getStatusColor, getWinningColor} from '../../utils/formatters';
import {useTableUrlState} from "../../hooks/useTableUrlState"; // Re-using our formatters

const GamesPage = () => {
    // Data state from the API
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    // API fetching state
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const {pagination, setPagination, sorting, setSorting} = useTableUrlState({
        initialSorting: [{id: 'startedAt', desc: true}],
        initialPageSize: 20
    });

    // Data fetching logic
    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });

        if (sorting.length > 0) {
            params.append('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        }

        apiService.get(`/api/games?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [pagination, sorting]);

    // Trigger fetch when pagination or sorting state changes
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Column definitions
    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Game ID',
                size: 320,
                Cell: ({ cell }) => (
                    <Link component={RouterLink} to={`/games/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorFn: row => row.player?.name || row.player?.id,
                id: 'playerUid',
                header: 'Player',
                size: 100,
                Cell: ({row}) => {
                    const {player} = row.original;
                    if (!player?.id) {
                        return '—';
                    }
                    const target = `/players/${player.id}`;
                    return (
                        <Link component={RouterLink} to={target} underline="none">
                            {formatPlayerName(player)}
                        </Link>
                    );
                },
            },
            {
                accessorKey: 'machineId',
                header: 'Machine',
                size: 100,
                Cell: ({ cell }) => (
                    <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>
                        {cell.getValue().slice(0, 8)}…
                    </Link>
                ),
            },
            {
                accessorKey: 'status',
                header: 'Status',
                Cell: ({ cell }) => (
                    <Chip label={cell.getValue()} color={getStatusColor(cell.getValue())} size="small" />
                ),
            },
            {
                accessorKey: 'winning',
                header: 'Result',
                size: 100,
                Cell: ({cell}) => {
                    const winning = cell.getValue();
                    if (!winning) return '—';
                    return <Chip label={winning} color={getWinningColor(winning)} size="small"/>;
                },
            },
            {
                accessorKey: 'startedAt',
                header: 'Started',
                Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
            },
            {
                accessorKey: 'finishedAt',
                header: 'Finished',
                Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—',
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data,
        // Manual (server-side) data handling
        manualPagination: true,
        manualSorting: true,
        rowCount,
        // State management
        state: {
            pagination,
            sorting,
            isLoading,
            showAlertBanner: isError,
        },
        // State update handlers
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        // UI Configurations
        muiToolbarAlertBannerProps: isError
            ? { color: 'error', children: 'Error loading data' }
            : undefined,
        initialState: {
            showGlobalFilter: true,
        },
    });

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                <Typography variant="h4">Games</Typography>
                <Button
                    component={RouterLink}
                    to="/games/stats"
                    variant="outlined"
                    startIcon={<AssessmentIcon/>}
                >
                    Statistics
                </Button>
            </Stack>
            <MaterialReactTable table={table} />
        </Box>
    );
};

export default GamesPage;